import type { JSX } from "react"
import fp from "lodash/fp"
import { createSelector } from "@reduxjs/toolkit"
import { Box } from "@mui/material"

import type { TFunction } from "i18next"

import type { RootState } from "features/store"
import type { EntityTelemetry } from "types/telemetries.types"
import type { FieldConversion, RecordType } from "types/dashboard.types"
import { TOOLTIP_IMAGES, TOOLTIP_IMAGE_PREFIX } from "helpers/config/tooltipImages"
import {
  AMPACITY_AAR_CRITICAL_SPAN_FIELD,
  AMPACITY_DLR_CRITICAL_SPAN_FIELD,
} from "helpers/config/telemetryFieldDefs"
import { formatFilename } from "./common"

type FormatTelemetryPlotData = (
  data: EntityTelemetry[] | undefined,
  fieldConversions?: { [fieldName: string]: FieldConversion },
) => EntityTelemetry[]
export const formatTelemetryPlotData: FormatTelemetryPlotData = (
  data,
  fieldConversions,
) =>
  (data || []).map((d) => {
    const value = d._value

    // Always convert booleans to numbers for plotting
    if (fp.isBoolean(value)) {
      return { ...d, _value: value ? 1 : 0 }
    }

    // Apply field-specific conversions
    if (fieldConversions && d._field) {
      const conversion = fieldConversions[d._field]
      if (conversion === "radiansToDegrees" && typeof value === "number") {
        return { ...d, _value: (value * 180) / Math.PI }
      }
    }

    return d
  })

/**
 * Lookup for a legend parts, which is the record containing the legend
 *
 * @param data
 * @returns the legend found or undefined if none was present
 */
export const getLegendParts = (data?: RecordType[]): RecordType | undefined => {
  const record = data?.find((record) => record.legend)
  return record
}

export const formatPlotTitleTooltip = (
  tooltip: string[],
  t: TFunction,
  language?: string,
): JSX.Element[] => {
  const isEs = language?.startsWith("es")
  return tooltip.map((item) => {
    // Items prefixed with "img:" render a locale-aware image instead of text.
    if (item.startsWith(TOOLTIP_IMAGE_PREFIX)) {
      const img = TOOLTIP_IMAGES[item.slice(TOOLTIP_IMAGE_PREFIX.length)]
      if (!img) return <div key={item} />
      return (
        <Box
          key={item}
          component="img"
          src={isEs ? img.es : img.en}
          alt=""
          sx={{ width: "100%", maxWidth: 280, display: "block", borderRadius: 1 }}
        />
      )
    }
    return <div key={item}>{t(item)}</div>
  })
}

export const getPlotLoadingKey = (values: {
  fields?: string[]
  aggregate?: string[]
  method?: string[]
  entity?: string
}) => {
  const { fields, aggregate, method, entity } = values
  return JSON.stringify({
    ...(entity && { entity }),
    ...(aggregate && { aggregate: [...aggregate].sort() }),
    ...(fields && { fields: [...fields].sort() }),
    ...(method && { method: [...method].sort() }),
  })
}

const TELEMETRY_QUERY_MAP: Record<string, string> = {
  weatherService: "weatherService",
  deviceTelemetry: "device",
  powerlineTelemetry: "powerline",
  towerTelemetry: "tower",
  spanTelemetry: "span",
}

const isLimitingSpansQuery = (field?: string[]): boolean => {
  return (
    Array.isArray(field) &&
    field.length === 2 &&
    field.includes(AMPACITY_AAR_CRITICAL_SPAN_FIELD) &&
    field.includes(AMPACITY_DLR_CRITICAL_SPAN_FIELD)
  )
}

// Extract entity from query key (e.g., "deviceTelemetry(...)" -> "device")
const extractEntity = (queryKey: string): string | undefined => {
  for (const [queryType, entity] of Object.entries(TELEMETRY_QUERY_MAP)) {
    if (queryKey.startsWith(`${queryType}(`)) {
      return entity
    }
  }
  return undefined
}

export const selectPlotsLoadingByKey = createSelector(
  (state: RootState) => state.api.queries,
  (queries) => {
    const result: Record<string, boolean> = {}
    for (const key in queries) {
      const entity = extractEntity(key)
      if (entity) {
        const query = queries[key]
        const { field, aggregate, method } =
          ((query?.originalArgs as any)?.params as any) || {}

        // Exclude limiting spans query (independent from date picker)
        if (isLimitingSpansQuery(field)) {
          continue
        }

        const queryKey = getPlotLoadingKey({
          fields: field,
          aggregate,
          method,
          entity,
        })
        if (query?.status === "pending") {
          result[queryKey] = true
        } else if (result[queryKey] === undefined) {
          result[queryKey] = false
        }
      }
    }
    return result
  },
)

export const selectArePlotsLoading = createSelector(
  selectPlotsLoadingByKey,
  (loadingByKey) => Object.values(loadingByKey).some((isLoading) => isLoading),
)

export const generateCSVFilename = (
  entity: string,
  entityIds: (number | string)[],
  nameKey: string,
  formattedFromDate: string,
  formattedtoDate: string,
  t: (key: string, options?: any) => string,
): string => {
  // Max 3 ids shown in filename
  const idString =
    entityIds.length <= 3
      ? entityIds.join("_")
      : `${entityIds.slice(0, 3).join("_")}_${t("generic.AND_X_MORE", { count: entityIds.length - 3 })}`

  const rawBaseName = `${t(`entities.${entity.toUpperCase()}`)}_${idString}_${t(`plot_titles.${nameKey}`)}_${formattedFromDate}_${t("generic.TO")}_${formattedtoDate}`

  return formatFilename(rawBaseName, "csv")
}

export const generateRawCSVFilename = (
  entity: string,
  entityId: number | string,
  formattedFromDate: string,
  formattedToDate: string,
  t: (key: string) => string,
): string => {
  const rawBaseName = `${t(`entities.${entity.toUpperCase()}`)}_${entityId}_raw_${formattedFromDate}_${t("generic.TO")}_${formattedToDate}`
  return formatFilename(rawBaseName, "csv")
}
